// components/Documents/CreateCategoryDialog.jsx
import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import CustomInput from "../FormFields/CustomInput";

const CreateCategoryDialog = ({
  open,
  onClose,
  onSave,
  categoryName,
  setCategoryName,
  editingCategory,
  isLoading = false,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "80vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 0, m: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 4,
            py: 3,
            bgcolor: "#FFFFFF",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FolderIcon sx={{ color: theme.palette.primary.main, fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#0F172A"
                fontSize="1.25rem"
                letterSpacing="-0.02em"
              >
                {editingCategory ? "Edit Category" : "Create New Category"}
              </Typography>
              <Typography
                variant="body2"
                color="#64748B"
                fontSize="0.875rem"
                fontWeight={400}
              >
                {editingCategory
                  ? "Update the category name"
                  : "Add a new category to organize documents"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="medium"
            sx={{
              color: "#94A3B8",
              bgcolor: "#F1F5F9",
              "&:hover": { bgcolor: "#E2E8F0", color: "#475569" },
            }}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "#F1F5F9" }} />
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: "#F8FAFC", flex: 1 }}>
        <Box sx={{ p: 4 }}>
          <CustomInput
            autoFocus
            fullWidth
            label="Category Name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            placeholder="Enter category name..."
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#FFFFFF",
                borderRadius: "12px",
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #F1F5F9",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 4,
            py: 1.25,
            color: "#64748B",
            borderColor: "#E2E8F0",
            "&:hover": {
              borderColor: "#94A3B8",
              bgcolor: "#F8FAFC",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={isLoading || !categoryName.trim()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 4,
            py: 1.25,
            bgcolor: theme.palette.primary.main,
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
              boxShadow: `0px 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            "&.Mui-disabled": {
              bgcolor: "#E2E8F0",
              color: "#94A3B8",
            },
          }}
        >
          {isLoading ? "Saving..." : editingCategory ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCategoryDialog;